import React, { useState, useMemo, useEffect } from 'react';
import { FormControl, Modal, Box } from '@mui/material';
import { MultiUserSelect, MultiUserSelector } from 'components/Selectors/MulltiUserSelector';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { reloadScopes, updateScopes, updateTeam, updateVisibleAccounts } from 'lib/redux/users/slice';
import { ACCOUNT_COVERAGE_ID_ALL, ACCOUNT_COVERAGE_ID_NA, ScopeAccountCoverageType, ScopeHolderType, ScopeType, ScopeUserCoverageType, ScopeV2, USER_COVERAGE_ID_ALL } from "interfaces/users";
import { UserDataResult } from "interfaces/users";
import { Loader } from 'components/Loader';
import { getServicesManager } from 'services';
import { Typography } from 'interfaces/typography';
import { SearchInput, PrimaryActionButton, STANDARD_CONTAINER_CLASSES } from 'utils/CommonUIComponents';
import { TeamInvitation } from "interfaces/db";
import { uniqueBy } from "core";
import { Mail, UserPlus, X, Edit3, UserMinus } from 'lucide-react';
import { useUserData } from '../../lib/redux/users/user-data-hook';



const Toggle: React.FC<{ disabled: boolean, value: boolean; onChange: () => void; label: string }> = ({ disabled, value, onChange, label }) => (
  <button
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
      value ? 'bg-blue-600' : 'bg-gray-200'
    }`}
    aria-label={label}
  >
    <span
      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
        value ? 'translate-x-4' : 'translate-x-1'
      }`}
    />
  </button>
);

export function UserManagement() {
  const dispatch = useDispatch();
  const { user: viewingUser, allUsers: all_users, sortedUserGroupInfo, userGroupsLoaded, visibleAccounts, team, isAdmin, updateUser } = useUserData();
  const scopesResult = useSelector((state: RootState) => state.scope.value);
  
  // Calculate unassigned users
  const unassignedUsers = useMemo(() => {
    const assignedUserIds = new Set();
    sortedUserGroupInfo.forEach(g => g.directUserIds.forEach(userId => assignedUserIds.add(userId)));
    return all_users.filter(u => !assignedUserIds.has(u.user_id)).sort(
      (a, b) => a.user_name.localeCompare(b.user_name) || a.created_at.valueOf() - b.created_at.valueOf()
    );
  }, [all_users, sortedUserGroupInfo]);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');
  
  // Team invitation states
  const [sentInvitations, setSentInvitations] = useState<TeamInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteCanDial, setInviteCanDial] = useState<boolean>(true);
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState<boolean>(false);
  
  // Bulk invitation states
  const [bulkInviteEmails, setBulkInviteEmails] = useState<string>('');
  const [bulkInviteCanDial, setBulkInviteCanDial] = useState<boolean>(true);
  const [showBulkInviteModal, setShowBulkInviteModal] = useState<boolean>(false);
  const [showBulkInviteConfirm, setShowBulkInviteConfirm] = useState<boolean>(false);
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  
  // Bulk selection states
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showBulkRemoveConfirm, setShowBulkRemoveConfirm] = useState<boolean>(false);
  const [removingUsers, setRemovingUsers] = useState<boolean>(false);
  
  // Team rename states
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [renamingTeam, setRenamingTeam] = useState<boolean>(false);
  const teamName = team?.team ?? '';
  
  // Remove user states
  const [removingUserId, setRemovingUserId] = useState<string>('');
  const [showRemoveUserModal, setShowRemoveUserModal] = useState<boolean>(false);
  const [removingUser, setRemovingUser] = useState<boolean>(false);
  const removingUserData = all_users.find(u => u.user_id === removingUserId);
  
  React.useEffect(() => {
    setNewTeamName(teamName);
  }, [teamName]);

  const activeUsers = useMemo(() => {
    return all_users.filter(user => user.team_is_active).length;
  }, [all_users]);

  const activeAndCanDialUsers = useMemo(() => {
    return all_users.filter(user => user.team_is_active && user.can_dial).length;
  }, [all_users]);

  useEffect(() => {
    if (!scopesResult) reloadScopes(dispatch)
    // Load sent invitations
    getServicesManager().getSentTeamInvitations().then(sentInvitations => {
      if (sentInvitations) { setSentInvitations(sentInvitations) }
    })
  }, [dispatch, scopesResult])

  const updateActive = async (user: UserDataResult, isActive: boolean) => {
    // Store original value for revert
    const originalValue = user.team_is_active
    
    // Optimistically update UI immediately
    updateUser(user.user_id, { team_is_active: isActive })
    
    try {
      // Make API call
      const success = await getServicesManager().putSeats(user.user_id, isActive, user.can_dial)
      
      if (!success) {
        // Revert on failure
        updateUser(user.user_id, { team_is_active: originalValue })
        console.error('Failed to update user active status - reverted')
      }
    } catch (error) {
      // Revert on error
      updateUser(user.user_id, { team_is_active: originalValue })
      console.error('Failed to update user active status:', error)
    }
  }

  const updateCanDial = async (user: UserDataResult, canDial: boolean) => {
    // Store original value for revert
    const originalValue = user.can_dial
    
    // Optimistically update UI immediately
    updateUser(user.user_id, { can_dial: canDial })
    
    try {
      // Make API call
      const success = await getServicesManager().putSeats(user.user_id, user.team_is_active, canDial)
      
      if (!success) {
        // Revert on failure
        updateUser(user.user_id, { can_dial: originalValue })
        console.error('Failed to update user can dial status - reverted')
      }
    } catch (error) {
      // Revert on error
      updateUser(user.user_id, { can_dial: originalValue })
      console.error('Failed to update user can dial status:', error)
    }
  }

  const getDefaultAdminScope = (user: UserDataResult): ScopeV2 => {
    return {
      holder_id: user.user_id,
      holder_type: ScopeHolderType.USER,
      user_coverage_type: ScopeUserCoverageType.TEAM,
      scope_type: ScopeType.ADMIN_WRITE,
      holder_team_id: user.team_id,
      user_coverage_id: user.team_id,
      account_coverage_type: ScopeAccountCoverageType.NA,
      account_coverage_id: ACCOUNT_COVERAGE_ID_NA
    }
  }
  const getCurrentAdminScope = (user: UserDataResult): ScopeV2 | undefined => {
    return scopesResult?.scopes.find(s => s.scope_type === ScopeType.ADMIN_WRITE && s.holder_id === user.user_id && s.holder_type === ScopeHolderType.USER && s.user_coverage_type === ScopeUserCoverageType.TEAM)
  }
  const updateAdminState = (user: UserDataResult, isAdmin: boolean) => {
    if (!scopesResult) return
    const currentScope = getCurrentAdminScope(user)
    if (!isAdmin) {
      if (!currentScope) return
      dispatch(updateScopes({...scopesResult, 'scopes': scopesResult.scopes.filter(s => JSON.stringify(s) !== JSON.stringify(currentScope))}))
      getServicesManager().updateScope(currentScope, true)
    } else {
      if (currentScope) return
      const newScope = getDefaultAdminScope(user)
      dispatch(updateScopes({...scopesResult, 'scopes': [...scopesResult.scopes, newScope]}))
      getServicesManager().updateScope(newScope)
    }
  }

  // Bulk selection helpers
  const toggleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else if (newSelected.size < 5) { // Limit to 5 users
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };


  const clearSelection = () => {
    setSelectedUserIds(new Set());
  };

  const getSelectedUsers = () => {
    return filteredUsers.filter(u => selectedUserIds.has(u.user_id));
  };

  // Bulk invite helpers
  const parseEmailsFromText = (text: string): string[] => {
    const emails = text
      .split(/[,;\n\r\t\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0 && email.includes('@'))
      .filter((email, index, array) => array.indexOf(email) === index);
    return emails;
  };

  const handleBulkInviteSubmit = () => {
    const emails = parseEmailsFromText(bulkInviteEmails);
    setParsedEmails(emails);
    if (emails.length > 0) {
      setShowBulkInviteConfirm(true);
    }
  };

  const confirmBulkInvite = async () => {
    const promises = parsedEmails.map(email => 
      getServicesManager().createTeamInvitation(email, bulkInviteCanDial)
    );
    
    const results = await Promise.allSettled(promises);
    const successful = results
      .map((result) => result.status === 'fulfilled' && result.value ? result.value : null)
      .filter((invitation): invitation is TeamInvitation => invitation !== null);
    
    if (successful.length > 0) {
      setSentInvitations(sentInvitations => 
        uniqueBy([...sentInvitations, ...successful], i => i.email)
      );
    }
    
    setBulkInviteEmails('');
    setBulkInviteCanDial(true);
    setShowBulkInviteModal(false);
    setShowBulkInviteConfirm(false);
    setParsedEmails([]);
  };

  // Bulk remove helpers
  const confirmBulkRemove = async () => {
    setRemovingUsers(true);
    const userIds = Array.from(selectedUserIds);
    
    const promises = userIds.map(userId => 
      getServicesManager().removeUserFromCurrentTeam(userId)
    );
    
    const results = await Promise.allSettled(promises);
    const successfulRemovals = userIds.filter((userId, index) => 
      results[index].status === 'fulfilled' && (results[index] as PromiseFulfilledResult<any>).value
    );
    
    if (successfulRemovals.length > 0 && visibleAccounts) {
      const updatedUsers = visibleAccounts.users.filter(u => 
        !successfulRemovals.includes(u.user_id)
      );
      dispatch(updateVisibleAccounts({
        teams: visibleAccounts.teams,
        users: updatedUsers
      }));
    }
    
    setSelectedUserIds(new Set());
    setShowBulkRemoveConfirm(false);
    setRemovingUsers(false);
  };

  const filteredUsers = useMemo(() => {
    return all_users.filter(user => {
      // find group they are part of
      const matchingGroups = sortedUserGroupInfo.filter(g => g.directUserIds.includes(user.user_id))
      const groupNames = matchingGroups.map(g => g.group.user_group_name)
      const hasMatchingGroupName = groupNames.some(name => name.toLowerCase().includes(searchQuery.toLowerCase()))
      const matchesSearch = user.user_name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = 
        filterStatus === 'all' ? true :
        filterStatus === 'active' ? user.team_is_active :
        !user.team_is_active;
      return (hasMatchingGroupName || matchesSearch) && matchesFilter;
    });
  }, [all_users, filterStatus, searchQuery, sortedUserGroupInfo]);

  const unassignedFilteredUsers = useMemo(() => {
    const filtered_user_ids = filteredUsers.map(u => u.user_id)
    return unassignedUsers.filter(u => filtered_user_ids.includes(u.user_id))
  }, [filteredUsers, unassignedUsers])

  const filteredGroups = useMemo(() => {
    return sortedUserGroupInfo.filter((group) => {
      const matchesSearch = group.group.user_group_name.toLowerCase().includes(searchQuery.toLowerCase());
      const userMatches = filteredUsers.some((v) => group.directUserIds.includes(v.user_id));
      return group.directUserIds.length > 0 && ((matchesSearch && searchQuery !== '') || userMatches);
    });
  }, [sortedUserGroupInfo, filteredUsers, searchQuery]);

  if (!userGroupsLoaded || !scopesResult) return <Loader />;

  const convertScopeToMultiUserSelect = (scope: ScopeV2): MultiUserSelect => {
    switch (scope.user_coverage_type) {
      case ScopeUserCoverageType.TEAM: return { team_id: scope.user_coverage_id, 'user_group_id': null, 'user_id': null }
      case ScopeUserCoverageType.USER: return { user_id: scope.user_coverage_id, 'team_id': null, 'user_group_id': null }
      case ScopeUserCoverageType.USER_GROUP: return { user_group_id: scope.user_coverage_id, 'team_id': null, 'user_id': null }
      case ScopeUserCoverageType.ALL: return { team_id: viewingUser?.team_id ?? null, user_group_id: null, user_id: null }
    }
  }
  const convertMultiUserSelectToScope = (select: MultiUserSelect, id: string, scopeType: ScopeHolderType): ScopeV2 => {
    return {
      scope_type: ScopeType.SESSIONS_READ,
      holder_id: id,
      holder_type: scopeType,
      holder_team_id: viewingUser?.team_id ?? '',
      user_coverage_type: select.team_id ? ScopeUserCoverageType.ALL : select.user_group_id ? ScopeUserCoverageType.USER_GROUP : ScopeUserCoverageType.USER,
      user_coverage_id: (select.team_id ? USER_COVERAGE_ID_ALL : select.user_group_id ? select.user_group_id : select.user_id) ?? '',
      account_coverage_type: ScopeAccountCoverageType.ALL,  // # will be NA for scopes other than session_read
      account_coverage_id: ACCOUNT_COVERAGE_ID_ALL  // takes special values ACCOUNT_COVERAGE_ID_ALL, ACCOUNT_COVERAGE_ID_NA for corresponding types
    }
  }

  const getTeamReadScopes = (): ScopeV2[] => {
    return scopesResult.scopes.filter(s => s.holder_id === viewingUser?.team_id && s.scope_type === ScopeType.SESSIONS_READ && s.holder_type === ScopeHolderType.TEAM)
  }

  const getSelectedReadScopesForTeam = (): MultiUserSelect[] => {
    const readScopes = getTeamReadScopes()
    return readScopes.map((v) => convertScopeToMultiUserSelect(v))
  }

  const updateSelectedReadScopeTeam = (selected: MultiUserSelect[]) => {
    // need to delete some scopes and add some scopes...
    const currentReadScopes = getTeamReadScopes()
    const toDeleteScopes = currentReadScopes.filter((v) => {
      const multi_select = convertScopeToMultiUserSelect(v)
      return !selected.some((s) => s.team_id === multi_select.team_id && s.user_id === multi_select.user_id && s.user_group_id === multi_select.user_group_id)
    })
    const toAddSelects = selected.filter((s) => {
      return !currentReadScopes.some((v) => {
        const multi_select = convertScopeToMultiUserSelect(v)
        return multi_select.team_id === s.team_id && multi_select.user_id === s.user_id && multi_select.user_group_id === s.user_group_id
      })
    }).map((s) => convertMultiUserSelectToScope(s, viewingUser?.team_id ?? '', ScopeHolderType.TEAM))
    dispatch(updateScopes({...scopesResult, 'scopes': [...scopesResult.scopes.filter((v) => !toDeleteScopes.includes(v)), ...toAddSelects]}))
    for (const scope of toDeleteScopes) {
      getServicesManager().updateScope(scope, true)
    }
    for (const scope of toAddSelects) {
      getServicesManager().updateScope(scope)
    }
  }


  const getCurrentReadScopes = (id: string, type: ScopeHolderType): ScopeV2[] => {
    return scopesResult.scopes.filter(s => s.holder_id === id && s.scope_type === ScopeType.SESSIONS_READ && s.holder_type === type && (s.user_coverage_id !== id || s.user_coverage_type !== ScopeUserCoverageType.USER)) 
  }
  const getSelectedReadScopes = (id: string, type: ScopeHolderType): MultiUserSelect[] => {
    const readScopes = getCurrentReadScopes(id, type)
    return readScopes.map((v) => convertScopeToMultiUserSelect(v))
  }
  const updateSelectedReadScope = (id: string, scopeType: ScopeHolderType, selected: MultiUserSelect[]) => {
    // need to delete some scopes and add some scopes...
    const currentReadScopes = getCurrentReadScopes(id, scopeType)
    const toDeleteScopes = currentReadScopes.filter((v) => {
      const multi_select = convertScopeToMultiUserSelect(v)
      return !selected.some((s) => s.team_id === multi_select.team_id && s.user_id === multi_select.user_id && s.user_group_id === multi_select.user_group_id)
    })
    const toAddSelects = selected.filter((s) => {
      return !currentReadScopes.some((v) => {
        const multi_select = convertScopeToMultiUserSelect(v)
        return multi_select.team_id === s.team_id && multi_select.user_id === s.user_id && multi_select.user_group_id === s.user_group_id
      })
    }).map((s) => convertMultiUserSelectToScope(s, id, scopeType))
    dispatch(updateScopes({...scopesResult, 'scopes': [...scopesResult.scopes.filter((v) => !toDeleteScopes.includes(v)), ...toAddSelects]}))
    for (const scope of toDeleteScopes) {
      getServicesManager().updateScope(scope, true)
    }
    for (const scope of toAddSelects) {
      getServicesManager().updateScope(scope)
    }
  }

  const renderUser = (userId: string) => {
    const user = filteredUsers.find(u => u.user_id === userId);
    if (!user) return null;
    const userIsAdmin = getCurrentAdminScope(user) !== undefined
    const canSelect = user.user_id !== viewingUser?.user_id;
    return <tr key={user.user_id} className="hover:bg-gray-50" style={{
      backgroundColor: user.user_id === viewingUser?.user_id ? 'rgba(79, 70, 229, 0.1)' : undefined
    }}>
      <td className="px-3 py-1 whitespace-nowrap pl-6">
        <div className="flex items-center gap-3">
          {canSelect && (
            <label className="relative flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUserIds.has(user.user_id)}
                onChange={() => toggleUserSelection(user.user_id)}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                selectedUserIds.has(user.user_id)
                  ? 'bg-blue-600 border-blue-600'
                  : 'bg-white border-gray-300 hover:border-blue-400'
              }`}>
                {selectedUserIds.has(user.user_id) && (
                  <svg className="w-3 h-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </label>
          )}
          <Typography variant="mediumParagraph" color="#1F2937">
            {user.user_name}
          </Typography>
        </div>
      </td>
      <td className="px-3 py-1 whitespace-nowrap">
        <FormControl fullWidth size="small">
          <MultiUserSelector 
          selectedUserOrGroups={getSelectedReadScopes(user.user_id, ScopeHolderType.USER)}
          onSelectedUserOrGroupsChange={(selected) => updateSelectedReadScope(user.user_id, ScopeHolderType.USER, selected)}
          />
      </FormControl>
      </td>
      <td className="px-3 py-1 whitespace-nowrap text-center">
        <Toggle
          disabled={user.user_id === viewingUser?.user_id}
          value={user.team_is_active}
          onChange={() => updateActive(user, !user.team_is_active)}
          label="Active toggle"
        />
      </td>
      <td className="px-3 py-1 whitespace-nowrap text-center">
        <Toggle
          disabled={false}
          value={user.can_dial}
          onChange={() => updateCanDial(user, !user.can_dial)}
          label="Can dial toggle"
        />
      </td>
      <td className="px-3 py-1 whitespace-nowrap text-center">
        <Toggle
          disabled={user.user_id === viewingUser?.user_id}
          value={userIsAdmin}
          onChange={() => updateAdminState(user, !userIsAdmin)}
          label="Admin toggle"
        />
      </td>
      {isAdmin && (
        <td className="px-3 py-1 whitespace-nowrap text-center">
          {user.user_id !== viewingUser?.user_id && (
            <button
              onClick={() => {
                setRemovingUserId(user.user_id);
                setShowRemoveUserModal(true);
              }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
            >
              <UserMinus size={12} />
              Remove
            </button>
          )}
        </td>
      )}
    </tr>
  }

  return (
    <div className="p-4 bg-gray-50">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'users' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Users
            </button>
            <button
              onClick={() => setActiveTab('invitations')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                activeTab === 'invitations' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Invitations
            </button>
          </div>
          
          {activeTab === 'users' && (
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search users..."
              className="flex-1 max-w-xs"
            />
          )}
        </div>
        
        <div className="flex items-center gap-4">
          {activeTab === 'users' && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                <div className="flex items-center gap-1">
                  <Typography variant="smallParagraph" color="#6B7280">
                    Active Users:
                  </Typography>
                  <Typography variant="h5" color="#1F2937" fontWeight={600}>
                    {activeUsers}
                  </Typography>
                </div>
              </div>
              
              <div className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                <div className="flex items-center gap-1">
                  <Typography variant="smallParagraph" color="#6B7280">
                    Dial Access:
                  </Typography>
                  <Typography variant="h5" color="#1F2937" fontWeight={600}>
                    {activeAndCanDialUsers}
                  </Typography>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Typography variant="smallParagraph" color="#6B7280">
                  Show:
                </Typography>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => setFilterStatus('active')}
                    className={`px-3 py-2 text-xs font-medium transition-colors ${
                      filterStatus === 'active' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{
                      fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                      fontSize: '11px',
                      minWidth: '70px'
                    }}
                  >
                    Active Only
                  </button>
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-3 py-2 text-xs font-medium transition-colors border-l border-gray-300 ${
                      filterStatus === 'all' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{
                      fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                      fontSize: '11px',
                      minWidth: '70px'
                    }}
                  >
                    All Users
                  </button>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'invitations' && (
            <div></div>
          )}
        </div>
      </div>

      {activeTab === 'users' ? (
        <div className="space-y-4">
          {/* Bulk Actions Bar */}
          {selectedUserIds.size > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                    {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
                    {selectedUserIds.size === 5 && <span className="text-sm text-amber-600 ml-2">(max 5)</span>}
                  </Typography>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  {isAdmin && (
                    <button
                      onClick={() => setShowBulkRemoveConfirm(true)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium transition-colors"
                    >
                      <UserMinus size={14} />
                      Remove {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
          
          <div className="bg-white rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th scope="col" className="px-3 py-1">
                    <Typography variant="caption" color="#6B7280" style={{ textAlign: 'left' }}>
                      User/Team
                    </Typography>
                  </th>
                <th scope="col" className="px-3 py-1">
                  <Typography variant="caption" color="#6B7280" style={{ display: 'flex', justifyContent: 'center' }}>
                    Read Access
                  </Typography>
                </th>
                <th scope="col" className="px-3 py-1">
                  <Typography variant="caption" color="#6B7280" style={{ display: 'flex', justifyContent: 'center' }}>
                    Active
                  </Typography>
                </th>
                <th scope="col" className="px-3 py-1">
                  <Typography variant="caption" color="#6B7280" style={{ display: 'flex', justifyContent: 'center' }}>
                    Can Dial
                  </Typography>
                </th>
                <th scope="col" className="px-3 py-1">
                  <Typography variant="caption" color="#6B7280" style={{ display: 'flex', justifyContent: 'center' }}>
                    Admin
                  </Typography>
                </th>
                {isAdmin && (
                  <th scope="col" className="px-3 py-1">
                    <Typography variant="caption" color="#6B7280" style={{ display: 'flex', justifyContent: 'center' }}>
                      Actions
                    </Typography>
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
            <tr className="bg-blue-50">
                <td className="px-3 py-1 text-left">
                  <Typography variant="h5" color="#1F2937" fontWeight={600} style={{ textAlign: 'left' }}>
                    Overall Team
                  </Typography>
                </td>
                <td className="px-3 py-1 whitespace-nowrap">
                  <FormControl fullWidth size="small">
                    <MultiUserSelector 
                    selectedUserOrGroups={getSelectedReadScopesForTeam()}
                    onSelectedUserOrGroupsChange={(selected) => updateSelectedReadScopeTeam(selected)}
                    />
                  </FormControl>
                </td>
                <td colSpan={isAdmin ? 4 : 3} className=''></td>
              </tr>
              {filteredGroups.map((group) => (
                <React.Fragment key={group.group.user_group_id}>
                  <tr className="bg-gray-50">
                    <td className="px-3 py-1 text-left">
                      <Typography variant="h5" color="#1F2937" fontWeight={600} style={{ textAlign: 'left' }}>
                        {group.group.user_group_name}
                      </Typography>
                    </td>
                    <td className="px-3 py-1 whitespace-nowrap">
                        <FormControl fullWidth size="small">
                          <MultiUserSelector 
                          selectedUserOrGroups={getSelectedReadScopes(group.group.user_group_id, ScopeHolderType.USER_GROUP)}
                          onSelectedUserOrGroupsChange={(selected) => updateSelectedReadScope(group.group.user_group_id, ScopeHolderType.USER_GROUP, selected)}
                          />
                      </FormControl>
                      </td>
                      <td colSpan={isAdmin ? 4 : 3} className=''></td>
                  </tr>
                  {group.directUserIds.map(user => renderUser(user))}
                </React.Fragment>
              ))}
              {unassignedFilteredUsers.length > 0 && <tr className="bg-gray-50">
                <td colSpan={isAdmin ? 6 : 5} className="px-3 py-1 text-left">
                  <Typography variant="h5" color="#1F2937" fontWeight={600} style={{ textAlign: 'left' }}>
                    Unassigned
                  </Typography>
                </td>
              </tr>}
              {unassignedFilteredUsers.map(user => renderUser(user.user_id))}
            </tbody>
          </table>
        </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Current Team Section */}
          <div className={STANDARD_CONTAINER_CLASSES} style={{ borderRadius: '8px', padding: '1.5rem' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserPlus size={20} className="text-blue-600" />
                </div>
                <div>
                  <Typography variant="h4" color="#1F2937" fontWeight={600}>
                    Current Team
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Manage your team settings and invitations
                  </Typography>
                </div>
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowRenameModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                >
                  <Edit3 size={14} />
                  Edit
                </button>
              )}
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center border border-gray-200">
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={600}>
                    {teamName.charAt(0).toUpperCase()}
                  </Typography>
                </div>
                <div>
                  <Typography variant="h5" color="#1F2937" fontWeight={600}>
                    {teamName || 'Unnamed Team'}
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    {activeUsers} active user{activeUsers !== 1 ? 's' : ''} • {activeAndCanDialUsers} can dial
                  </Typography>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sent Invitations Section */}
          <div className={STANDARD_CONTAINER_CLASSES} style={{ borderRadius: '8px', padding: '1.5rem' }}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Mail size={20} className="text-gray-700" />
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Sent Invitations
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    if (!isAdmin || team?.subscription_type !== 'TEAM') {
                      setShowUpgradeModal(true);
                    } else {
                      setShowInviteModal(true);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <div className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full">
                    <UserPlus size={14} />
                  </div>
                  <Typography variant="mediumParagraph" color="white" fontWeight={500}>
                    Send Invitation
                  </Typography>
                </button>
                <button
                  onClick={() => {
                    if (!isAdmin || team?.subscription_type !== 'TEAM') {
                      setShowUpgradeModal(true);
                    } else {
                      setShowBulkInviteModal(true);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 rounded-md transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400"
                >
                  <div className="flex items-center justify-center w-5 h-5">
                    <Mail size={14} />
                  </div>
                  <Typography variant="mediumParagraph" color="inherit" fontWeight={500}>
                    Bulk Invite
                  </Typography>
                </button>
              </div>
            </div>
          
          {sentInvitations.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <Mail size={24} className="text-gray-400" />
              </div>
              <Typography variant="mediumParagraph" color="#6B7280" style={{ marginBottom: '0.5rem' }}>
                No invitations sent yet
              </Typography>
              <Typography variant="smallParagraph" color="#9CA3AF">
                Start by sending your first team invitation
              </Typography>
            </div>
          ) : (
            <div className="space-y-3">
              {sentInvitations.map(invitation => (
                <div key={invitation.email} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail size={14} className="text-blue-600" />
                    </div>
                    <div>
                      <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                        {invitation.email}
                      </Typography>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                          invitation.can_dial ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {invitation.can_dial ? 'Can Dial' : 'View Only'}
                        </span>
                        <Typography variant="smallParagraph" color="#6B7280">
                          Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      getServicesManager().rescindTeamInvitation(invitation.email).then(numDeleted => {
                        if (numDeleted !== null) {
                          setSentInvitations(sentInvitations => 
                            sentInvitations.filter(i => i.email !== invitation.email)
                          );
                        }
                      });
                    }}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                  >
                    <X size={14} />
                    Rescind
                  </button>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}
      
      {/* Invite Modal */}
      <Modal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '450px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <UserPlus size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Send Team Invitation
                </Typography>
              </div>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Email Address
              </Typography>
              <input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inviteCanDial}
                  onChange={(e) => setInviteCanDial(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                    Can dial (grant phone access)
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Allow this user to make phone calls through the platform
                  </Typography>
                </div>
              </label>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowInviteModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={<Mail size={16} />}
              onClick={() => {
                if (inviteEmail.trim()) {
                  getServicesManager().createTeamInvitation(inviteEmail, inviteCanDial).then(newInvitation => {
                    if (newInvitation) {
                      setSentInvitations(sentInvitations => uniqueBy([...sentInvitations, newInvitation], i => i.email));
                      setInviteEmail('');
                      setInviteCanDial(true);
                      setShowInviteModal(false);
                    }
                  });
                }
              }}
              disabled={!inviteEmail.trim()}
            >
              Send Invitation
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Upgrade Modal */}
      <Modal
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <UserPlus size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Upgrade to Team Account
                </Typography>
              </div>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <UserPlus size={24} className="text-blue-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Team Features Required
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                To invite team members, you need to upgrade to a team account.
              </Typography>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.75rem' }}>
                Team Account Benefits:
              </Typography>
              <ul className="space-y-2">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Send team invitations
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Manage user permissions
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Advanced team analytics
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Priority support
                  </Typography>
                </li>
              </ul>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <Typography variant="mediumParagraph" color="#1e40af" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Ready to upgrade?
              </Typography>
              <Typography variant="smallParagraph" color="#374151">
                Contact our team at{' '}
                <a 
                  href="mailto:support@trellus.ai" 
                  className="text-blue-600 hover:text-blue-700 font-medium underline"
                >
                  support@trellus.ai
                </a>
                {' '}to activate your team account.
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Maybe Later
            </button>
            <a
              href="mailto:support@trellus.ai?subject=Team Account Upgrade Request"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium text-sm transition-colors"
            >
              <Mail size={16} />
              Contact Team
            </a>
          </div>
        </Box>
      </Modal>
      
      {/* Team Rename Modal */}
      <Modal
        open={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '450px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Edit3 size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Rename Team
                </Typography>
              </div>
              <button
                onClick={() => setShowRenameModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-4">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Team Name
              </Typography>
              <input
                type="text"
                placeholder="Enter new team name"
                value={newTeamName}
                onChange={(e) => setNewTeamName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="smallParagraph" color="#6B7280">
                This will update your team name for all members. Current name: <strong>{teamName}</strong>
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowRenameModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={renamingTeam ? null : <Edit3 size={16} />}
              onClick={() => {
                if (newTeamName.trim() && newTeamName.trim() !== teamName) {
                  setRenamingTeam(true);
                  getServicesManager().postSettings({is_team: true, team: newTeamName.trim()}).then(() => {
                    setShowRenameModal(false);
                    if (team) {
                      dispatch(updateTeam({ ...team, team: newTeamName.trim() }));
                    }
                  }).finally(() => {
                    setRenamingTeam(false);
                  });
                }
              }}
              disabled={!newTeamName.trim() || newTeamName.trim() === teamName || renamingTeam}
            >
              {renamingTeam ? (
                <div className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Renaming...
                </div>
              ) : (
                'Rename Team'
              )}
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Remove User Modal */}
      <Modal
        open={showRemoveUserModal}
        onClose={() => setShowRemoveUserModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-red-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <UserMinus size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Remove Team Member
                </Typography>
              </div>
              <button
                onClick={() => setShowRemoveUserModal(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <UserMinus size={24} className="text-red-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Remove User from Team
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                Are you sure you want to remove <strong>{removingUserData?.user_name}</strong> from your team?
              </Typography>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <Typography variant="mediumParagraph" color="#dc2626" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Warning: This action cannot be undone
              </Typography>
              <Typography variant="smallParagraph" color="#7f1d1d">
                The user will be moved to their own team and will lose access to all team data, settings, and shared resources.
              </Typography>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                What happens next:
              </Typography>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    {removingUserData?.user_name} will be moved to a new individual team
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    They will lose access to current team data and settings
                  </Typography>
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-gray-400 rounded-full"></div>
                  <Typography variant="smallParagraph" color="#6B7280">
                    They can be re-invited to the team later if needed
                  </Typography>
                </li>
              </ul>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowRemoveUserModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (removingUserId) {
                  setRemovingUser(true);
                  getServicesManager().removeUserFromCurrentTeam(removingUserId).then(success => {
                    if (success) {
                      setShowRemoveUserModal(false);
                      setRemovingUserId('');
                      // Update Redux state directly by removing the user from visibleAccounts
                      if (visibleAccounts) {
                        const updatedUsers = visibleAccounts.users.filter(u => u.user_id !== removingUserId);
                        dispatch(updateVisibleAccounts({
                          teams: visibleAccounts.teams,
                          users: updatedUsers
                        }));
                      }
                    }
                  }).finally(() => {
                    setRemovingUser(false);
                  });
                }
              }}
              disabled={removingUser}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-md font-medium text-sm transition-colors"
            >
              {removingUser ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Removing...
                </>
              ) : (
                <>
                  <UserMinus size={16} />
                  Remove {removingUserData?.user_name}
                </>
              )}
            </button>
          </div>
        </Box>
      </Modal>
      
      {/* Bulk Remove Confirmation Modal */}
      <Modal
        open={showBulkRemoveConfirm}
        onClose={() => !removingUsers && setShowBulkRemoveConfirm(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-red-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <UserMinus size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Remove Multiple Users
                </Typography>
              </div>
              {!removingUsers && (
                <button
                  onClick={() => setShowBulkRemoveConfirm(false)}
                  className="text-white/80 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <UserMinus size={24} className="text-red-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Remove {selectedUserIds.size} User{selectedUserIds.size !== 1 ? 's' : ''} from Team
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                Are you sure you want to remove the following users from your team?
              </Typography>
            </div>
            
            {/* Selected users list */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-40 overflow-y-auto">
              <div className="space-y-2">
                {getSelectedUsers().map(user => (
                  <div key={user.user_id} className="flex items-center gap-3 p-2 bg-white rounded border">
                    <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                      <Typography variant="smallParagraph" color="#374151" fontWeight={600}>
                        {user.user_name.charAt(0).toUpperCase()}
                      </Typography>
                    </div>
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                      {user.user_name}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <Typography variant="mediumParagraph" color="#dc2626" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Warning: This action cannot be undone
              </Typography>
              <Typography variant="smallParagraph" color="#7f1d1d">
                All selected users will be moved to their own teams and lose access to team data, settings, and shared resources.
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowBulkRemoveConfirm(false)}
              disabled={removingUsers}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={confirmBulkRemove}
              disabled={removingUsers}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-md font-medium text-sm transition-colors"
            >
              {removingUsers ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Removing {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}...
                </>
              ) : (
                <>
                  <UserMinus size={16} />
                  Remove {selectedUserIds.size} User{selectedUserIds.size !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </Box>
      </Modal>
      
      {/* Bulk Invite Modal */}
      <Modal
        open={showBulkInviteModal}
        onClose={() => setShowBulkInviteModal(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <Mail size={16} className="text-blue-600" />
                </div>
                <Typography variant="h4" color="#1F2937" fontWeight={600}>
                  Bulk Team Invitations
                </Typography>
              </div>
              <button
                onClick={() => setShowBulkInviteModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 space-y-6">
            <div>
              <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Email Addresses
              </Typography>
              <Typography variant="smallParagraph" color="#6B7280" style={{ marginBottom: '1rem' }}>
                Enter multiple email addresses separated by commas, spaces, or on new lines
              </Typography>
              <textarea
                placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                value={bulkInviteEmails}
                onChange={(e) => setBulkInviteEmails(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical"
                style={{
                  fontFamily: 'Calibri,Arial,Helvetica,sans-serif',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#374151',
                  minHeight: '120px'
                }}
              />
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={bulkInviteCanDial}
                  onChange={(e) => setBulkInviteCanDial(e.target.checked)}
                  className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <Typography variant="mediumParagraph" color="#374151" fontWeight={500} style={{ marginBottom: '0.25rem' }}>
                    Can dial (grant phone access)
                  </Typography>
                  <Typography variant="smallParagraph" color="#6B7280">
                    Allow all invited users to make phone calls through the platform
                  </Typography>
                </div>
              </label>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={() => setShowBulkInviteModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Cancel
            </button>
            <PrimaryActionButton
              icon={<UserPlus size={16} />}
              onClick={handleBulkInviteSubmit}
              disabled={!bulkInviteEmails.trim()}
            >
              Review Invitations
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
      
      {/* Bulk Invite Confirmation Modal */}
      <Modal
        open={showBulkInviteConfirm}
        onClose={() => setShowBulkInviteConfirm(false)}
        closeAfterTransition
        sx={{display: 'flex', flexFlow: 'column', alignItems: 'center', justifyContent: 'center'}}
      >
        <Box sx={{
          position: 'relative', maxWidth: '90vw', maxHeight: '90vh', zIndex: 1, minWidth: '500px',
          backgroundColor: 'white', padding: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          display: 'flex', flexFlow: 'column', alignItems: 'stretch', borderRadius: '12px',
          overflow: 'hidden'
        }}>
          {/* Modal Header */}
          <div className="bg-blue-500 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <Mail size={16} className="text-white" />
                </div>
                <Typography variant="h4" color="white" fontWeight={600}>
                  Confirm Bulk Invitations
                </Typography>
              </div>
              <button
                onClick={() => setShowBulkInviteConfirm(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          
          {/* Modal Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail size={24} className="text-blue-600" />
              </div>
              <Typography variant="h4" color="#1F2937" fontWeight={600} style={{ marginBottom: '0.5rem' }}>
                Send {parsedEmails.length} Invitation{parsedEmails.length !== 1 ? 's' : ''}
              </Typography>
              <Typography variant="mediumParagraph" color="#6B7280">
                Review the email addresses below and confirm to send team invitations:
              </Typography>
            </div>
            
            {/* Email list */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto">
              <div className="space-y-2">
                {parsedEmails.map((email, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-white rounded border">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Mail size={14} className="text-blue-600" />
                    </div>
                    <Typography variant="mediumParagraph" color="#1F2937" fontWeight={500}>
                      {email}
                    </Typography>
                    <div className="ml-auto">
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                        bulkInviteCanDial ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {bulkInviteCanDial ? 'Can Dial' : 'View Only'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <Typography variant="mediumParagraph" color="#1e40af" fontWeight={500} style={{ marginBottom: '0.5rem' }}>
                Double-check before sending
              </Typography>
              <Typography variant="smallParagraph" color="#374151">
                Each person will receive an email invitation to join your team with the access level shown above.
              </Typography>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={() => setShowBulkInviteConfirm(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              Back to Edit
            </button>
            <PrimaryActionButton
              icon={<Mail size={16} />}
              onClick={confirmBulkInvite}
            >
              Send {parsedEmails.length} Invitation{parsedEmails.length !== 1 ? 's' : ''}
            </PrimaryActionButton>
          </div>
        </Box>
      </Modal>
    </div>
  );
}


